import * as React from 'react';
import { FunctionComponent, useContext, useEffect, useRef } from 'react';

// OpenLayers
import Control from 'ol/control/Control';

// MUI Components
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material';

// Custom components
import MapContext from '@/components/Map/MapContext';
import CoordZoomStatus from '@/components/Map/Controls/CoordZoomStatus/coordzoomstatus';

// Lib
import { findControl, getDefinedOptions, getEvents } from '@/lib/olHelpers';
import { getOptimalTextColorForBackgroundColor } from '@/lib/colorUtilities';

// Types
import { MapContextType } from '@/@types/context/MapContext';
import { ICordZoomStatusRoot } from '@/@types/components/Map/Controls/CordZoomStatus';

const CoordZoomStatusRoot: FunctionComponent<ICordZoomStatusRoot> = (props) => {
  const mapContext = useContext(MapContext) as MapContextType;

  const { ref, id, isPublic } = props;

  const coordzoomstatusRef = useRef(ref);

  const theme = useTheme();

  useEffect(() => {
    if (coordzoomstatusRef.current) {
      const options = {
        element: coordzoomstatusRef.current,
      };

      const allOptions = Object.assign(options, props);
      const definedOptions = getDefinedOptions(allOptions);

      const events = {};

      const control = new CoordZoomStatus(definedOptions);

      if (id) {
        control.set('id', id);
      }

      if (mapContext.map) {
        const mapControl = findControl(mapContext.map, id, CoordZoomStatus);
        if (mapControl) {
          mapContext.map.removeControl(mapControl);
        }
        mapContext.map.addControl(control as Control);
      } else {
        mapContext.initOptions.controls.push(control as Control);
      }

      const olEvents = getEvents(events, props);
      for (const eventName in olEvents) {
        // @ts-ignore TODO:  Argument of type 'string' is not assignable to parameter of type '("error" | "change" | "propertychange")[]'
        control.on(eventName, olEvents[eventName]);
      }
    }

    return () => {
      if (mapContext.map) {
        const mapControl = findControl(mapContext.map, id, CoordZoomStatus);
        if (mapControl) {
          mapContext.map.removeControl(mapControl);
        }
      }
    };
  }, []);

  return (
    <Box
      id="coordzoomstatus-control"
      sx={{
        display: 'flex',
        position: 'absolute',
        bottom: 8,
        right: isPublic ? 64 : 8,

        maxWidth: 500,
        height: 20,

        color: getOptimalTextColorForBackgroundColor(theme.palette.primary.main),
        backgroundColor: isPublic ? 'unset' : 'primary.main',
        opacity: isPublic ? 1 : 0.75,

        textShadow: isPublic ?  "0px 0px 3px #000" : "none",
        pr: 0.5,
        borderRadius: '4px',
      }}
      ref={coordzoomstatusRef}
    >
      {props.children}
    </Box>
  );
};

export default CoordZoomStatusRoot;
